import { useSphereDialog } from "@components/common/dialog/sphere-dialog-provider";
import { useIsXgSeatRequiredForRole } from "@components/table/members/assign-seats/use-is-xg-seat-required-for-role";
import { XgSeatRequiredForRoleDialog } from "@components/table/members/assign-seats/xg-seat-required-for-role-dialog";
import { Text } from "@flat-ui";
import { useChangeMemberRole } from "@hooks/use-change-member-role";
import { formatUserRoleType } from "@utils/data-display";
import { WorkspaceMemberEvents } from "@utils/track-event/track-event-list";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { useState } from "react";
/**
 * Changes the workspace role of a single member or team, with the same checks wherever the role
 * is changed: a team's role change is confirmed first, and a role that needs a Sphere XG seat asks
 * for the seat first when the member does not hold one (see useIsXgSeatRequiredForRole).
 */
export function useChangeWorkspaceRole({
  companyId,
  memberIdentity,
  currentRole,
  member,
  location,
  teamName,
  onSettled
}) {
  const {
    trackEvent
  } = useTrackEvent();
  const {
    createDialog
  } = useSphereDialog();
  const isXgSeatRequiredForRole = useIsXgSeatRequiredForRole(member);
  const [isUpdating, setIsUpdating] = useState(false);

  // The role picked for a member that needs a Sphere XG seat for it first
  const [roleAwaitingXgSeat, setRoleAwaitingXgSeat] = useState();
  const {
    changeMemberRole
  } = useChangeMemberRole({
    companyId,
    location,
    subjectType: "workspace",
    memberIdentity,
    currentRole
  });
  const xgSeatDialog = member && roleAwaitingXgSeat ? <XgSeatRequiredForRoleDialog member={member} role={roleAwaitingXgSeat} events={WorkspaceMemberEvents} onClose={closeXgSeatDialog} onSeatAssigned={() => applyRoleChange(roleAwaitingXgSeat)} /> : null;
  return {
    changeRole,
    isUpdating,
    xgSeatDialog
  };
  async function changeRole(role) {
    // Picking the current role again changes nothing, so it needs neither a seat nor tracking
    if (role === currentRole) {
      onSettled?.();
      return;
    }
    trackEvent({
      name: WorkspaceMemberEvents.changeRole,
      props: {
        role
      }
    });
    if (isUpdating) {
      onSettled?.();
      return;
    }

    // Changing a team's role affects every member of the team, so confirm first.
    if (teamName && !(await confirmTeamRoleChange(role))) {
      onSettled?.();
      return;
    }

    // The role needs a Sphere XG seat, which the dialog assigns before it changes the role
    if (isXgSeatRequiredForRole(role)) {
      trackEvent({
        name: WorkspaceMemberEvents.openXgSeatRequiredForRole,
        props: {
          role,
          location
        }
      });
      setRoleAwaitingXgSeat(role);
      return;
    }
    await applyRoleChange(role);
    onSettled?.();
  }

  /** Closes the Sphere XG seat dialog, whether the role was changed or not */
  function closeXgSeatDialog() {
    setRoleAwaitingXgSeat(undefined);
    onSettled?.();
  }

  /** Changes the role of the member, marking it as updating meanwhile */
  async function applyRoleChange(role_0) {
    try {
      setIsUpdating(true);
      await changeMemberRole(role_0);
    } catch {
      // Errors are already handled by the slice, only notify about a successful result
    } finally {
      setIsUpdating(false);
    }
  }

  /** Ask the user to confirm changing the role for the whole team */
  async function confirmTeamRoleChange(role_1) {
    return createDialog({
      title: "Change Workspace Role",
      confirmText: "Change Role",
      isHideClose: true
    }, <Text variant="bodyM">
        Are you sure you want to change the role of{" "}
        <var>
          <strong>{teamName}</strong>{" "}
        </var>
        to <strong>{formatUserRoleType(role_1)}</strong>? This will affect all
        members of this team.
      </Text>);
  }
}